import { mm, Viewer } from "@inovitas/infra3dsdk";
import { Scene } from "@inovitas/infra3dsdk/dist/types/mm";

import ClientConfig from "shared/config";

export interface LinRefConfig {
  type: "rail-ch" | "rbbs-mistra" | "rbbs" | "rbbs-abs";
  project: string;
  // If no ref_point_attribute is provided, the "bezugspunkte" are not shown in the AxisScaleBar.
  ref_point_attribute?: string;
}

export function addLinRefLayers(linrefconfig: LinRefConfig, viewer: Viewer) {
  const layerhandler = viewer.getScene().getLayerHandler();

  const networklayerconfig = getNetworkLayerConfig(linrefconfig);

  if (!networklayerconfig) return;

  layerhandler.addLayer(mm.LAYERTYPE.NETWORKLAYER, {
    ...networklayerconfig,
    axisloader: getAxisloader(linrefconfig, viewer.getScene()),
  });
}

function getAxisloader(linrefconfig: LinRefConfig, scene: Scene) {
  switch (linrefconfig.type) {
    case "rbbs-mistra":
      return new mm.MistraAxisLoader(scene, {
        baseurl: ClientConfig.mistraaxisloaderURL,
      });
    case "rbbs-abs":
      return new mm.AxisGRLoader(scene, {
        baseurl: ClientConfig.geofeatureserverURL,
        project: linrefconfig.project,
      });
    case "rail-ch":
      return new mm.RailPosLoader(scene, {
        baseurl: ClientConfig.geofeatureserverURL,
        project: linrefconfig.project,
      });
    case "rbbs":
      return new mm.AxisAGLoader(scene, {
        baseurl: ClientConfig.geofeatureserverURL,
        project: linrefconfig.project,
      });
  }
}

function getNetworkLayerConfig(linrefconfig: LinRefConfig) {
  switch (linrefconfig.type) {
    case "rbbs-mistra":
      return {
        hideinlayertree: true,
        referencedate: true,
        axisidattributearray: ["SECTOR_AXIS_SEGMENT_KEY", "SECTOR_NAME"],
        nominallengthattribute: "SECTOR_LENGTH",
        axisnameattribute: "SECTOR_AXIS_SEGMENT_KEY",
        axistypeattribute: "AXIS_TYPE_DE",
        visible: true,
        strategyoptions: {
          boxdimension: 10000,
          bordertolerance: 3000,
        },
        style: {
          stylename: "layerstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [0.051, 0.573, 0.824, 1],
              },
            },
          ],
        },
        highlightstyle: {
          stylename: "highlightstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [1.0, 0.404, 0.0, 1],
              },
            },
          ],
        },
      };
    case "rbbs-abs":
      return {
        hideinlayertree: true,
        referencedate: false,
        axisidattributearray: ["axisname", "sektornr"],
        nominallengthattribute: "nominallength",
        axisnameattribute: "axisname",
        axistypeattribute: "",
        visible: true,
        strategyoptions: {
          boxdimension: 2000,
          bordertolerance: 500,
        },
        style: {
          stylename: "layerstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [0.051, 0.573, 0.824, 1],
              },
            },
          ],
        },
        highlightstyle: {
          stylename: "highlightstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [1.0, 0.404, 0.0, 1],
              },
            },
          ],
        },
      };
    case "rail-ch":
      return {
        hideinlayertree: true,
        referencedate: false,
        axisidattributearray: ["name"],
        nominallengthattribute: "elementlae",
        offsetattribute: "kmoffset",
        axisnameattribute: "name",
        axistypeattribute: "type",
        visible: true,
        nonavigationtypes: ["Gleisachse"],
        style: {
          stylename: "layerstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [0.051, 0.573, 0.824, 1],
              },
            },
          ],
        },
        highlightstyle: {
          stylename: "highlightstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [1.0, 0.404, 0.0, 1],
              },
            },
          ],
        },
        strategyoptions: {
          boxdimension: 1500,
          bordertolerance: 500,
        },
        alignsettings: {
          v_tolerance_realign: 15,
          u_border_tolerance_realign: 5,
          v_clip_onaxis: 10,
          v_clip_outofaxis: 12,
          n_frame_samples: 8,
          v_tolerance_initial: 200,
        },
      };
    case "rbbs":
      return {
        hideinlayertree: true,
        referencedate: false,
        axisidattributearray: ["OWNER", "AXISNAME", "SECTORNAME"],
        nominallengthattribute: "SECTORLENGTH",
        axisnameattribute: "name",
        axistypeattribute: "type",
        visible: true,
        strategyoptions: {
          boxdimension: 2000,
          bordertolerance: 500,
        },
        style: {
          stylename: "layerstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [0.051, 0.573, 0.824, 1],
              },
            },
          ],
        },
        highlightstyle: {
          stylename: "highlightstyle",
          rules: [
            {
              filter: "",
              symbolizer: "line",
              params: {
                "stroke-width": 2,
                stroke: [1.0, 0.404, 0.0, 1],
              },
            },
          ],
        },
      };
    default:
      throw new Error(
        `Unsupported linref type: ${linrefconfig.type}. Supported types are: rail-ch, rbbs-mistra, rbbs-abs, rbbs.`
      );
  }
}
